import React from 'react';
import { CheckCircle2, XCircle, Loader2, X, UploadCloud } from 'lucide-react';
import { useUploadManager } from '../../context/UploadManagerContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Bottom-right tray that shows every in-flight or recently-finished upload.
 *
 * Rules:
 *   * Only mounted for admin users (super or regular). Regular customers
 *     never see it — they don't upload files.
 *   * Auto-hides when the list is empty.
 *   * A single "collapsed" pill when everything is done; expands on hover
 *     for individual rows. Failed uploads stay pinned until the admin
 *     dismisses them (they carry the actionable error message from the
 *     backend so they mustn't disappear on their own).
 */
const UploadTray = () => {
  const { user } = useAuth() || {};
  const mgr = useUploadManager();
  if (!user?.is_admin) return null;
  if (!mgr || mgr.uploads.length === 0) return null;

  const { uploads, dismiss, activeCount } = mgr;
  const anyErrors = uploads.some(u => u.status === 'error');

  return (
    <div
      data-testid="upload-tray"
      className="fixed bottom-4 right-4 z-[70] w-[min(360px,calc(100vw-2rem))] bg-white border border-neutral-200 rounded-sm shadow-2xl overflow-hidden"
    >
      <div className={`px-3 py-2 flex items-center justify-between text-white ${anyErrors && activeCount === 0 ? 'bg-red-600' : 'bg-[#12263A]'}`}>
        <div className="flex items-center gap-2 text-sm font-bold">
          {activeCount > 0
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {activeCount}…</>
            : anyErrors
              ? <><XCircle className="w-4 h-4" /> Upload failed</>
              : <><CheckCircle2 className="w-4 h-4" /> Uploads complete</>
          }
        </div>
        {activeCount === 0 && (
          <button
            onClick={() => uploads.forEach(u => dismiss(u.id))}
            className="text-white/70 hover:text-white"
            title="Dismiss all"
            data-testid="upload-tray-dismiss-all"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-neutral-100">
        {uploads.map(u => (
          <div key={u.id} className="px-3 py-2 flex items-center gap-3" data-testid={`upload-row-${u.id}`}>
            <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
              {u.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-[#F46A25]" />}
              {u.status === 'success'   && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              {u.status === 'error'     && <XCircle className="w-4 h-4 text-red-600" />}
              {u.status === 'queued'    && <UploadCloud className="w-4 h-4 text-neutral-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#12263A] truncate">{u.label}</div>
              {u.status === 'uploading' && (
                <div className="mt-1 h-1 bg-neutral-200 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#F46A25] transition-all duration-200" style={{ width: `${u.progress || 0}%` }} />
                </div>
              )}
              {u.status === 'error' && (
                <div className="text-[10px] text-red-600 mt-0.5 line-clamp-2">{u.error}</div>
              )}
              {u.status === 'success' && (
                <div className="text-[10px] text-emerald-700 mt-0.5">Saved{u.persistent === false ? ' (local only)' : ''}</div>
              )}
            </div>
            {u.status !== 'uploading' && (
              <button onClick={() => dismiss(u.id)} className="text-neutral-400 hover:text-[#12263A] flex-shrink-0" title="Dismiss" data-testid={`upload-row-dismiss-${u.id}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadTray;
